import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IUseChatHandlers,
  IUseCheckInitialUnreadChannels,
  IUseCheckUnreadChannel,
  IUseInitializeStreamClientConnection,
  IUseNewChannel,
  IUseSetInitialChannels,
} from '@core/chat/interfaces';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useChatState } from '@core/chat';

import { useRedirectToGlobalChat } from '@core/chat/hooks/channel';

import {
  checkHasUnreadPrivateChannel,
  checkIsStreamChannelUnread,
} from '../helpers';
import { EventHandler, StreamChat } from 'stream-chat';
import { DefaultStreamChatGenerics as StreamChatGenerics } from 'stream-chat-react/dist/types/types';
import { getCatchErrorMessage } from '@helpers/error';
import {
  useRedirectToPrivateChat,
  useStreamChannels,
} from '@core/chat/StreamChat/hooks';
import ChannelService from '@core/chat/services/ChannelService';
import { useSound } from '@hooks/media';
import NotificationSound from '@public/sounds/notification.wav';
import { ChatMessageType } from '@core/chat/components/ChatNotification';
import useNotificationMessage from '@core/chat/hooks/useNotificationMessage';
import { useNotificationContext } from '@providers/NotificationProvider';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import {
  checkChannelFormCurrentEvent,
  checkIsEventChannel,
  checkIsRoomChannel,
  getPrivateChannelId,
} from '@core/chat/helpers';

import { useNotification } from '@components/Notification';
import { NOTIFICATION_TYPES } from '@components/Notification/constants';
import { getNotification } from '@components/Notification/helpers';

const useStreamChatHandlers = ({
  currentEvent,
  streamChatToken,
}: IUseChatHandlers) => {
  const { user, configs } = useMytaverse();
  const { streamChatMemberId, participants } = useMytaverseEvent();
  const {
    open: isOpenChat,
    streamClient,
    setStreamClient,
    eventStreamChannel,
    eventRoomStreamChannel,
    setEventStreamChannel,
    privateChannels,
    setPrivateChannels,
    addPrivateChannel,
    setInitialLoading,
    setIsUnreadEventChannel,
    setIsUnreadPrivateChannel,
    setEventRoomStreamChannel,
    setIsUnreadEventRoomChannel,
  } = useChatState();

  const userId = streamChatMemberId || '';
  const currentEventId = currentEvent?.eventId || '';

  useInitializeStreamClientConnection({
    streamChatToken,
    streamClient,
    setStreamClient,
    user,
    streamChatMemberId: userId,
    configs,
  });

  useSetInitialChannels({
    streamClient,
    setEventStreamChannel,
    setPrivateChannels,
    setEventRoomStreamChannel,
    currentEventId,
    userId,
  });

  useCheckInitialUnreadChannels({
    eventChannel: eventStreamChannel,
    eventRoomChannel: eventRoomStreamChannel,
    privateChannels,
    setInitialLoading,
    setIsUnreadEventChannel,
    setIsUnreadPrivateChannel,
    setIsUnreadEventRoomChannel,
  });

  useCheckUnreadChannel({
    privateChannels,
    setIsUnreadEventChannel,
    setIsUnreadPrivateChannel,
    setIsUnreadEventRoomChannel,
    streamClient,
    userId,
    currentEventId,
    isOpenChat,
    participants,
  });

  useNewChannel({
    streamClient,
    hasEventStreamChannel: !!eventStreamChannel,
    setEventStreamChannel,
    addPrivateChannel,
    currentEventId,
    userId,
  });
};

const useInitializeStreamClientConnection = ({
  streamChatToken,
  streamClient,
  setStreamClient,
  user,
  streamChatMemberId,
  configs,
}: IUseInitializeStreamClientConnection) => {
  useEffect(() => {
    if (!streamChatMemberId || !streamChatToken) {
      return;
    }

    const initializeStreamClientConnection = async () => {
      try {
        const apiKey = configs?.streamChat?.tokens?.apiKey || '';
        const client = new StreamChat<StreamChatGenerics>(apiKey);

        const chatUser = {
          id: streamChatMemberId,
          name: streamChatMemberId,
        };
        await client.connectUser(chatUser, streamChatToken);

        setStreamClient(client);
      } catch (error: unknown) {
        throw Error(getCatchErrorMessage(error));
      }
    };

    initializeStreamClientConnection();

    return () => {
      streamClient?.disconnectUser();
      setStreamClient(null);
    };
  }, [user, streamChatToken, streamChatMemberId]);
};

const useSetInitialChannels = ({
  streamClient,
  setEventStreamChannel,
  setPrivateChannels,
  currentEventId,
  userId,
  setEventRoomStreamChannel,
}: IUseSetInitialChannels) => {
  const {
    getEventStreamChannel,
    getMapEventChannels,
    getEventRoomStreamChannel,
  } = useStreamChannels();
  const { currentRoom } = useMytaverseEvent();

  useEffect(() => {
    if (!streamClient || !currentEventId || !userId) {
      return;
    }

    const setInitialChannels = async () => {
      const eventStreamChannel = await getEventStreamChannel({
        streamClient,
        userId,
        eventId: currentEventId,
      });

      setEventStreamChannel(eventStreamChannel);

      const eventRoomStreamChannel = await getEventRoomStreamChannel({
        streamClient,
        userId,
        eventId: currentEventId,
        roomId: currentRoom?.roomId,
      });

      setEventRoomStreamChannel(eventRoomStreamChannel);

      const eventChannels = await ChannelService.getEventChannels(
        currentEventId,
      );
      const privateChannels = await getMapEventChannels(
        streamClient,
        eventChannels,
        userId,
      );

      setPrivateChannels(privateChannels.filter(({ channel }) => channel));
    };

    setInitialChannels();
  }, [streamClient, currentEventId, userId, currentRoom]);
};

const useCheckInitialUnreadChannels = ({
  eventChannel,
  eventRoomChannel,
  privateChannels,
  setInitialLoading,
  setIsUnreadEventChannel,
  setIsUnreadPrivateChannel,
  setIsUnreadEventRoomChannel,
}: IUseCheckInitialUnreadChannels) => {
  useEffect(() => {
    const isEventChannelUnread = checkIsStreamChannelUnread(eventChannel);

    setIsUnreadEventChannel(isEventChannelUnread);

    const isEventRoomChannelUnread =
      checkIsStreamChannelUnread(eventRoomChannel);

    setIsUnreadEventRoomChannel(isEventRoomChannelUnread);

    if (!privateChannels.length) {
      setInitialLoading(false);
      return;
    }

    const hasUnreadPrivateChannel =
      checkHasUnreadPrivateChannel(privateChannels);

    setIsUnreadPrivateChannel(hasUnreadPrivateChannel);
    setInitialLoading(false);
  }, [eventChannel, privateChannels, eventRoomChannel]);
};

const useCheckUnreadChannel = ({
  privateChannels,
  setIsUnreadEventChannel,
  setIsUnreadPrivateChannel,
  setIsUnreadEventRoomChannel,
  streamClient,
  userId,
  currentEventId,
  isOpenChat,
  participants,
}: IUseCheckUnreadChannel) => {
  const [play] = useSound(NotificationSound, {
    volume: 0.15,
  });
  const { showChatNotification } = useNotificationMessage();
  const redirectToPrivateChat = useRedirectToPrivateChat();
  const redirectToGlobalChat = useRedirectToGlobalChat();
  const { isEventRun } = useNotificationContext();
  const { currentRoom, isHideWebUIExceptAssistant, isHideAllExceptTopBar } =
    useMytaverseEvent();
  const { showNotification } = useNotification();
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (!streamClient) {
      return;
    }

    const handleNewMessage: EventHandler<StreamChatGenerics> = (evt) => {
      const participant = participants?.find(
        (participant) =>
          participant.streamChatMemberId === evt?.message?.user?.id ||
          participant.participantId === evt?.message?.user?.id,
      );

      if (evt?.message?.user && participant) {
        if (participant?.fullName) {
          evt.message.user.name = participant?.fullName;
        } else if (participant?.name) {
          evt.message.user.name = participant?.name;
        } else if (participant?.firstName || participant?.lastName) {
          if (participant?.firstName) {
            evt.message.user.name = participant?.firstName;
          }

          if (participant?.lastName) {
            evt.message.user.name += ` ${participant?.lastName}`;
          }
        }
      }

      if (!participant) {
        sendCustomSentryBreadcrumb({
          message:
            'Stream chat error unable to find participant data for user = ' +
            evt?.message?.user?.id,
        });
      }
      const streamChannelId = evt.channel_id || '';
      const readUserId = evt.user?.id || '';

      const isEventRoomChannel = checkIsRoomChannel(
        streamChannelId,
        currentRoom?.roomId as string,
      );

      if (isEventRoomChannel) {
        if (readUserId !== userId) {
          play();
          showNotification(
            getNotification({
              title: translate('notifications.broadcast'),
              message: evt.message?.text as string,
              type: NOTIFICATION_TYPES.SUCCESS,
              closeManually: true,
              withDismissButton: false,
            }),
          );
          setIsUnreadEventRoomChannel(true);
        }

        return;
      }

      const isCurrentUser = readUserId === userId;

      if (isCurrentUser || isOpenChat) {
        return;
      }

      const isCurrentEvent = checkChannelFormCurrentEvent(
        streamChannelId,
        currentEventId,
      );

      if (!isCurrentEvent) {
        return;
      }

      const isEventChannel = checkIsEventChannel(
        streamChannelId,
        currentEventId,
      );

      const isHideUi =
        isHideWebUIExceptAssistant.flag || isHideAllExceptTopBar.flag;

      if (!isEventRun || isHideUi) {
        return;
      }

      if (isEventChannel) {
        setIsUnreadEventChannel(true);
        play();
        showChatNotification({
          message: evt.message as ChatMessageType,
          onReply: redirectToGlobalChat,
        });
      } else {
        setIsUnreadPrivateChannel(true);
        play();
        showChatNotification({
          message: evt.message as ChatMessageType,
          onReply: redirectToPrivateChat,
        });
      }
    };

    const handleReadMessage: EventHandler<StreamChatGenerics> = (evt) => {
      const readUserId = evt.user?.id || '';
      const streamChannelId = evt.channel_id || '';
      const isCurrentUser = readUserId === userId;

      if (!isCurrentUser) {
        return;
      }

      const isEventRoomChannel = checkIsRoomChannel(
        streamChannelId,
        currentRoom?.roomId as string,
      );

      if (isEventRoomChannel) {
        setIsUnreadEventRoomChannel(false);
      }

      const isCurrentEvent = checkChannelFormCurrentEvent(
        streamChannelId,
        currentEventId,
      );

      if (!isCurrentEvent) {
        return;
      }

      const isEventChannel = checkIsEventChannel(
        streamChannelId,
        currentEventId,
      );

      if (isEventChannel) {
        setIsUnreadEventChannel(false);
      } else {
        const hasUnreadPrivateChannel =
          checkHasUnreadPrivateChannel(privateChannels);

        setIsUnreadPrivateChannel(hasUnreadPrivateChannel);
      }
    };

    const handleTruncateChannel: EventHandler<StreamChatGenerics> = (evt) => {
      const streamChannelId = evt.channel_id || '';

      const isEventRoomChannel = checkIsRoomChannel(
        streamChannelId,
        currentRoom?.roomId as string,
      );

      if (isEventRoomChannel) {
        setIsUnreadEventRoomChannel(false);
      }

      const isCurrentEvent = checkChannelFormCurrentEvent(
        streamChannelId,
        currentEventId,
      );

      if (!isCurrentEvent) {
        return;
      }

      const isEventChannel = checkIsEventChannel(
        streamChannelId,
        currentEventId,
      );

      if (isEventChannel) {
        setIsUnreadEventChannel(false);
        return;
      }

      const hasUnreadPrivateChannel =
        checkHasUnreadPrivateChannel(privateChannels);

      setIsUnreadPrivateChannel(hasUnreadPrivateChannel);
    };

    streamClient.on('message.new', handleNewMessage);
    streamClient.on('message.read', handleReadMessage);
    streamClient.on('channel.truncated', handleTruncateChannel);

    return () => {
      streamClient.off('message.new', handleNewMessage);
      streamClient.off('message.read', handleReadMessage);
      streamClient.off('channel.truncated', handleTruncateChannel);
    };
  }, [
    currentEventId,
    privateChannels,
    streamClient,
    userId,
    isOpenChat,
    isEventRun,
    participants,
    isHideWebUIExceptAssistant.flag,
    isHideAllExceptTopBar.flag,
    currentRoom?.roomId,
  ]);
};

const useNewChannel = ({
  streamClient,
  hasEventStreamChannel,
  setEventStreamChannel,
  addPrivateChannel,
  currentEventId,
  userId,
}: IUseNewChannel) => {
  const { getEventStreamChannel, getMapEventChannel } = useStreamChannels();

  useEffect(() => {
    if (!streamClient || !currentEventId || !userId) {
      return;
    }

    const newChannelSubscription = streamClient.on(
      'notification.added_to_channel',
      async (event) => {
        const streamChannelId = event.channel_id || '';
        const isCurrentEvent = checkChannelFormCurrentEvent(
          streamChannelId,
          currentEventId,
        );

        if (!isCurrentEvent) {
          return;
        }

        const isEventChannel = checkIsEventChannel(
          streamChannelId,
          currentEventId,
        );

        if (isEventChannel) {
          if (hasEventStreamChannel) {
            return;
          }

          const eventStreamChannel = await getEventStreamChannel({
            streamClient,
            userId: userId,
            eventId: currentEventId,
          });

          setEventStreamChannel(eventStreamChannel);
          return;
        }

        const privateChannelId = getPrivateChannelId(streamChannelId);
        const eventChannel = await ChannelService.getEventChannel(
          currentEventId,
          privateChannelId,
        );
        const newPrivateChannel = await getMapEventChannel(
          streamClient,
          eventChannel,
          userId,
        );

        addPrivateChannel(newPrivateChannel);
      },
    );

    return () => {
      newChannelSubscription.unsubscribe();
    };
  }, [streamClient, currentEventId, userId, hasEventStreamChannel]);
};

export default useStreamChatHandlers;
